/**
 * BlocksEditor — visual CMS for reorderable Content Blocks.
 *
 * Features:
 *  - Drag & drop reorder (dnd-kit) on the block handle
 *  - Duplicate / delete / collapse-expand per block
 *  - All 14 block types available from the "Add block" toolbar
 *  - Wi-Fi block: SSID + password + security selector + live QR preview + download PNG
 *  - YouTube block: URL, i18n title & description, format (16:9 vs 9:16), size
 */
import React, { useState, useRef } from "react";
import {
  ChevronUp,
  ChevronDown,
  Trash2,
  Plus,
  Youtube,
  Check,
  Copy as CopyIcon,
  GripVertical,
  Download,
  QrCode,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Input } from "@/components/ui/input";
import { LocalizedField } from "./_shared";
import ImageUploader from "@/components/ImageUploader";
import { extractYouTubeId } from "@/lib/helpers";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";

const BLOCK_TYPES = [
  { value: "heading", label: "Titolo" },
  { value: "text", label: "Testo" },
  { value: "wifi", label: "Wi-Fi" },
  { value: "checkin_out", label: "Check-in / out" },
  { value: "image", label: "Immagine" },
  { value: "gallery", label: "Galleria" },
  { value: "pdf", label: "PDF" },
  { value: "youtube", label: "Video YouTube" },
  { value: "button", label: "Pulsante" },
  { value: "link", label: "Link" },
  { value: "phone", label: "Telefono" },
  { value: "email", label: "Email" },
  { value: "map", label: "Mappa" },
  { value: "divider", label: "Divisore" },
];

const BLOCK_TYPE_LABELS = Object.fromEntries(BLOCK_TYPES.map((t) => [t.value, t.label]));

function newBlockId() {
  return `blk_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
}

function newBlock(type, order) {
  return { id: newBlockId(), type, order, data: {} };
}

/** Short 1-line preview of a block for the collapsed state header. */
function blockSummary(block) {
  const d = block.data || {};
  switch (block.type) {
    case "heading":
    case "text":
      return d.text?.it || d.text?.en || d.text?.de || "";
    case "wifi":
      return d.ssid ? `${d.ssid}` : "";
    case "checkin_out":
      return [d.checkin_time, d.checkout_time].filter(Boolean).join(" · ");
    case "image":
      return d.url ? "1 immagine" : "";
    case "gallery":
      return (d.images || []).length ? `${(d.images || []).length} immagini` : "";
    case "pdf":
      return d.label?.it || d.url || "";
    case "youtube":
      return d.videoId ? `YouTube · ${d.videoId}` : "";
    case "button":
    case "link":
      return d.label?.it || d.url || "";
    case "phone":
      return d.number || "";
    case "email":
      return d.email || "";
    case "map":
      return d.address || (d.lat && d.lng ? `${d.lat}, ${d.lng}` : "");
    default:
      return "";
  }
}

/** Convert an inline SVG element into a PNG data-URL. Used to download the QR. */
function svgElementToPngUrl(svgEl, size = 512) {
  return new Promise((resolve, reject) => {
    try {
      const clone = svgEl.cloneNode(true);
      clone.setAttribute("width", String(size));
      clone.setAttribute("height", String(size));
      const svgStr = new XMLSerializer().serializeToString(clone);
      const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    } catch (e) {
      reject(e);
    }
  });
}

/** Wi-Fi block editor: SSID + password + security + live QR preview + download. */
function WifiEditor({ block, setData }) {
  const ssid = block.data?.ssid || "";
  const password = block.data?.password || "";
  const security = block.data?.security || "WPA";
  const qrRef = useRef(null);

  const secToken = security === "NOPASS" ? "nopass" : security === "WEP" ? "WEP" : "WPA";
  const escape = (v) => String(v || "").replace(/([\\;,"':])/g, "\\$1");
  const payload = `WIFI:T:${secToken};S:${escape(ssid)};P:${escape(password)};H:false;;`;

  const download = async () => {
    if (!ssid) {
      toast.error("Inserisci prima l'SSID (nome rete)");
      return;
    }
    if (!password && security !== "NOPASS") {
      toast.error("Inserisci la password oppure imposta 'Nessuna password'");
      return;
    }
    try {
      const svg = qrRef.current?.querySelector("svg");
      if (!svg) return;
      const dataUrl = await svgElementToPngUrl(svg, 512);
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `wifi-${ssid.replace(/[^a-zA-Z0-9-_]/g, "_")}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("QR scaricato");
    } catch (e) {
      toast.error("Impossibile generare il PNG");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input
          placeholder="Nome rete (SSID)"
          value={ssid}
          onChange={(e) => setData({ ssid: e.target.value })}
          data-testid={`block-wifi-ssid-${block.id}`}
        />
        <Input
          placeholder="Password"
          value={password}
          onChange={(e) => setData({ password: e.target.value })}
          data-testid={`block-wifi-password-${block.id}`}
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-[11px] text-ink-500">Tipo di sicurezza</label>
        <select
          value={security}
          onChange={(e) => setData({ security: e.target.value })}
          className="text-xs rounded-lg border border-ink-200/60 bg-white px-2 py-1"
          data-testid={`block-wifi-security-${block.id}`}
        >
          <option value="WPA">WPA / WPA2 / WPA3</option>
          <option value="WEP">WEP</option>
          <option value="NOPASS">Nessuna password</option>
        </select>
      </div>
      {ssid && (password || security === "NOPASS") && (
        <div className="mt-1 flex items-center gap-3">
          <div ref={qrRef} className="p-1.5 rounded-lg bg-white border border-ink-200/40 shadow-soft">
            <QRCodeSVG value={payload} size={72} level="M" bgColor="#FFFFFF" fgColor="#2A2622" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] text-ink-500">
              Anteprima QR — verrà mostrato nella scheda appartamento.
            </div>
            <button
              type="button"
              onClick={download}
              className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-cream-100 hover:bg-corten-50 text-ink-700 hover:text-corten-500 text-xs px-3 py-1.5 transition"
              data-testid={`block-wifi-download-${block.id}`}
            >
              <Download className="w-3 h-3" /> Scarica QR (PNG)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** YouTube block editor with title/description i18n and format/size selectors. */
function YouTubeEditor({ block, setData }) {
  const vid = block.data?.videoId || "";
  const format = block.data?.format === "vertical" ? "vertical" : "horizontal";
  const size = ["small", "medium", "large", "full"].includes(block.data?.size)
    ? block.data.size
    : "full";
  const inputRef = useRef(null);
  return (
    <div className="flex flex-col gap-2">
      <Input
        ref={inputRef}
        placeholder="Incolla URL YouTube o ID (es. https://youtu.be/... o dQw4w9WgXcQ)"
        defaultValue={vid}
        onBlur={(e) => {
          const id = extractYouTubeId(e.target.value);
          setData({ videoId: id || "" });
          if (id) e.target.value = id;
        }}
        data-testid={`block-youtube-input-${block.id}`}
      />
      {vid ? (
        <div className="flex items-center gap-1.5 text-[11px] text-bronze-500" data-testid={`block-youtube-valid-${block.id}`}>
          <Check className="w-3 h-3" /> Video riconosciuto · ID: <span className="font-mono">{vid}</span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-[11px] text-ink-300">
          <Youtube className="w-3 h-3" /> Supporta youtube.com/watch, youtu.be, /embed, /shorts o solo l'ID
        </div>
      )}
      <LocalizedField
        label="Titolo (opzionale)"
        value={block.data?.title}
        onChange={(v) => setData({ title: v })}
        testid={`block-youtube-title-${block.id}`}
      />
      <LocalizedField
        label="Descrizione (opzionale)"
        value={block.data?.description}
        onChange={(v) => setData({ description: v })}
        multiline
        testid={`block-youtube-desc-${block.id}`}
      />
      <div className="grid grid-cols-2 gap-2 mt-1">
        <div>
          <label className="text-[11px] text-ink-500">Formato</label>
          <select
            value={format}
            onChange={(e) => setData({ format: e.target.value })}
            className="w-full mt-0.5 text-xs rounded-lg border border-ink-200/60 bg-white px-2 py-1.5"
            data-testid={`block-youtube-format-${block.id}`}
          >
            <option value="horizontal">Orizzontale 16:9</option>
            <option value="vertical">Verticale 9:16 (Shorts)</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] text-ink-500">Dimensione</label>
          <select
            value={size}
            onChange={(e) => setData({ size: e.target.value })}
            className="w-full mt-0.5 text-xs rounded-lg border border-ink-200/60 bg-white px-2 py-1.5"
            data-testid={`block-youtube-size-${block.id}`}
          >
            <option value="small">Piccolo</option>
            <option value="medium">Medio</option>
            <option value="large">Grande</option>
            <option value="full">Larghezza piena</option>
          </select>
        </div>
      </div>
      {vid && (
        <div className="mt-1">
          <div className="text-[11px] text-ink-500 mb-1">Anteprima</div>
          <div
            className={
              {
                small: "max-w-[200px]",
                medium: "max-w-[320px]",
                large: "max-w-[480px]",
                full: "max-w-full",
              }[size]
            }
          >
            <div
              className={`rounded-lg overflow-hidden bg-black/95 ${
                format === "vertical" ? "aspect-[9/16]" : "aspect-video"
              }`}
            >
              <iframe
                title="anteprima"
                src={`https://www.youtube.com/embed/${vid}`}
                className="w-full h-full"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Body of a single block (all type-specific fields). */
function BlockBody({ block, setData }) {
  return (
    <>
      {(block.type === "heading" || block.type === "text") && (
        <LocalizedField
          label="Testo"
          value={block.data?.text}
          onChange={(v) => setData({ text: v })}
          multiline={block.type === "text"}
          testid={`block-text-${block.id}`}
        />
      )}
      {block.type === "image" && (
        <div className="grid grid-cols-1 gap-2">
          <ImageUploader
            value={block.data?.url || null}
            onChange={(v) => setData({ url: v })}
            testid={`block-image-${block.id}`}
          />
          <LocalizedField
            label="Didascalia"
            value={block.data?.caption}
            onChange={(v) => setData({ caption: v })}
          />
        </div>
      )}
      {block.type === "gallery" && (
        <ImageUploader
          multiple
          value={(block.data?.images || []).map((i) => (typeof i === "string" ? i : i.url))}
          onChange={(v) => setData({ images: v.map((u) => ({ url: u })) })}
          testid={`block-gallery-${block.id}`}
        />
      )}
      {block.type === "pdf" && (
        <div className="grid grid-cols-1 gap-2">
          <Input
            placeholder="URL PDF"
            value={block.data?.url || ""}
            onChange={(e) => setData({ url: e.target.value })}
          />
          <LocalizedField
            label="Etichetta"
            value={block.data?.label}
            onChange={(v) => setData({ label: v })}
          />
        </div>
      )}
      {block.type === "youtube" && <YouTubeEditor block={block} setData={setData} />}
      {(block.type === "button" || block.type === "link") && (
        <div className="grid grid-cols-1 gap-2">
          <Input
            placeholder="URL"
            value={block.data?.url || ""}
            onChange={(e) => setData({ url: e.target.value })}
          />
          <LocalizedField
            label="Etichetta"
            value={block.data?.label}
            onChange={(v) => setData({ label: v })}
          />
        </div>
      )}
      {block.type === "phone" && (
        <div className="grid grid-cols-1 gap-2">
          <Input
            placeholder="+39 ..."
            value={block.data?.number || ""}
            onChange={(e) => setData({ number: e.target.value })}
          />
          <LocalizedField
            label="Etichetta"
            value={block.data?.label}
            onChange={(v) => setData({ label: v })}
          />
        </div>
      )}
      {block.type === "email" && (
        <div className="grid grid-cols-1 gap-2">
          <Input
            placeholder="email@dominio.it"
            value={block.data?.email || ""}
            onChange={(e) => setData({ email: e.target.value })}
          />
          <LocalizedField
            label="Etichetta"
            value={block.data?.label}
            onChange={(v) => setData({ label: v })}
          />
        </div>
      )}
      {block.type === "map" && (
        <div className="grid grid-cols-2 gap-2">
          <Input
            placeholder="Latitudine"
            type="number"
            value={block.data?.lat ?? ""}
            onChange={(e) => setData({ lat: Number(e.target.value) })}
          />
          <Input
            placeholder="Longitudine"
            type="number"
            value={block.data?.lng ?? ""}
            onChange={(e) => setData({ lng: Number(e.target.value) })}
          />
          <Input
            className="col-span-2"
            placeholder="Indirizzo"
            value={block.data?.address || ""}
            onChange={(e) => setData({ address: e.target.value })}
          />
        </div>
      )}
      {block.type === "divider" && (
        <p className="text-xs text-ink-300">Divisore ornamentale</p>
      )}
      {block.type === "wifi" && <WifiEditor block={block} setData={setData} />}
      {block.type === "checkin_out" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Input
            placeholder="Orario check-in (es. 15:00 – 20:00)"
            value={block.data?.checkin_time || ""}
            onChange={(e) => setData({ checkin_time: e.target.value })}
            data-testid={`block-checkin-in-${block.id}`}
          />
          <Input
            placeholder="Orario check-out (es. entro le 10:00)"
            value={block.data?.checkout_time || ""}
            onChange={(e) => setData({ checkout_time: e.target.value })}
            data-testid={`block-checkin-out-${block.id}`}
          />
        </div>
      )}
    </>
  );
}

/** A sortable block card: drag handle, header with collapse/duplicate/delete, body. */
function SortableBlock({ block, index, onChange, onDelete, onDuplicate }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });
  const [collapsed, setCollapsed] = useState(false);
  const setData = (patch) => onChange({ ...block, data: { ...block.data, ...patch } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : "auto",
    opacity: isDragging ? 0.85 : 1,
  };

  const summary = blockSummary(block);
  const typeLabel = BLOCK_TYPE_LABELS[block.type] || block.type;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-xl border border-ink-200/50 bg-cream-50 p-3 ${
        isDragging ? "shadow-elevated" : ""
      }`}
      data-testid={`block-card-${block.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <button
            {...attributes}
            {...listeners}
            type="button"
            aria-label="Trascina il blocco"
            data-testid={`block-drag-${block.id}`}
            className="p-1 text-ink-400 hover:text-ink-700 cursor-grab active:cursor-grabbing touch-none"
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-corten-500 shrink-0">
            {typeLabel}
          </span>
          {collapsed && summary && (
            <span className="text-xs text-ink-500 truncate">— {summary}</span>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            data-testid={`block-toggle-${block.id}`}
            className="p-1 text-ink-500 hover:text-corten-500"
            aria-label={collapsed ? "Espandi" : "Comprimi"}
          >
            {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
          <button
            type="button"
            onClick={onDuplicate}
            data-testid={`block-duplicate-${block.id}`}
            className="p-1 text-ink-500 hover:text-corten-500"
            aria-label="Duplica blocco"
          >
            <CopyIcon className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-testid={`block-delete-${block.id}`}
            className="p-1 text-ink-500 hover:text-red-600"
            aria-label="Elimina blocco"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {!collapsed && <BlockBody block={block} setData={setData} />}
    </div>
  );
}

export default function BlocksEditor({ blocks = [], onChange }) {
  const set = (list) => onChange(list.map((b, i) => ({ ...b, order: i })));

  const add = (type) => set([...(blocks || []), newBlock(type, blocks.length)]);
  const update = (idx, block) => {
    const c = [...blocks];
    c[idx] = block;
    set(c);
  };
  const del = (idx) => set(blocks.filter((_, i) => i !== idx));
  const duplicate = (idx) => {
    const src = blocks[idx];
    if (!src) return;
    const copy = {
      ...src,
      id: newBlockId(),
      // deep-copy data to detach from source
      data: JSON.parse(JSON.stringify(src.data || {})),
    };
    const next = [...blocks];
    next.splice(idx + 1, 0, copy);
    set(next);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = blocks.findIndex((b) => b.id === active.id);
    const newIdx = blocks.findIndex((b) => b.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    set(arrayMove(blocks, oldIdx, newIdx));
  };

  return (
    <div className="flex flex-col gap-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={(blocks || []).map((b) => b.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col gap-3">
            {(blocks || []).map((b, i) => (
              <SortableBlock
                key={b.id}
                block={b}
                index={i}
                onChange={(nb) => update(i, nb)}
                onDelete={() => del(i)}
                onDuplicate={() => duplicate(i)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className="flex flex-wrap gap-2 pt-1" data-testid="add-block-toolbar">
        {BLOCK_TYPES.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => add(t.value)}
            className="inline-flex items-center gap-1 rounded-full bg-cream-100 hover:bg-corten-50 text-ink-700 hover:text-corten-500 text-xs px-3 py-1.5 transition"
            data-testid={`add-block-${t.value}`}
          >
            <Plus className="w-3 h-3" />
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}
